/**
 * useTour — React hook for caller pages to drive a guided tour.
 *
 * Storage contract:
 *   `wayne.v2.tour.<employeeId>.<tourId>` → 'completed' | 'skipped'
 *
 * Once a record exists, `status` reflects it and `active` defaults to false
 * for the lifetime of the hook. Callers that want to replay the tour MUST
 * remove the storage key before calling `start()` (the orchestrator on the
 * page is responsible for that — `useTour` itself never deletes the key).
 *
 * If the user is unauthenticated, the key is namespaced under `anon` so a
 * never-logged-in browser still gets exactly one play.
 *
 * Per CLAUDE.md §4: storage value is a small enum, never PII or message
 * bodies; the `tourId` itself is a build-time constant.
 *
 * Hand-off note (Stream C): consumers call `useTour('home', steps)` and
 * pass `active` + handlers into `<GuidedTour>`. This hook is intentionally
 * thin — it owns persistence and "should this play?" decisions, not the
 * rendering of the tour.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import type { TourStep } from './types';

/** Persisted state of a tour for a single employee. */
export type TourStatus = 'unstarted' | 'completed' | 'skipped';

const STORAGE_PREFIX = 'wayne.v2.tour';

export interface UseTourReturn {
  /** Whether the tour is currently playing. Drives `<GuidedTour active={...}>`. */
  readonly active: boolean;
  /**
   * Start the tour. No-op if `status !== 'unstarted'` — callers that want to
   * replay must clear the storage key first.
   */
  readonly start: () => void;
  /** Mark as skipped, persist, and stop the tour. */
  readonly skip: () => void;
  /** Mark as completed, persist, and stop the tour. */
  readonly complete: () => void;
  /** The persisted status (or 'unstarted' if no record exists). */
  readonly status: TourStatus;
}

/** Build the localStorage key for a given employee + tour. */
export function tourStorageKey(
  tourId: string,
  employeeId: string | null | undefined,
): string {
  const owner = employeeId && employeeId.length > 0 ? employeeId : 'anon';
  return `${STORAGE_PREFIX}.${owner}.${tourId}`;
}

function readStatus(key: string): TourStatus {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === 'completed' || raw === 'skipped') {
      return raw;
    }
    return 'unstarted';
  } catch {
    // localStorage can throw in privacy/incognito modes — treat as unstarted.
    return 'unstarted';
  }
}

function writeStatus(key: string, value: 'completed' | 'skipped'): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Best-effort: if storage is unavailable we still close the tour for the
    // session — caller will see `active=false` until the page is reloaded.
  }
}

/**
 * Hook signature is locked for Stream C. The `steps` array is currently
 * unused here (only `<GuidedTour>` consumes it) but kept in the signature so
 * future logic — e.g. invalidating storage when the step list changes — can
 * land without a breaking API change.
 */
export function useTour(
  tourId: string,
  // Step list is part of the public surface but not used internally yet.
  // Underscore-prefixed so eslint's `no-unused-vars` rule (which ignores `^_`)
  // stays happy without an explicit override.
  _steps: ReadonlyArray<TourStep>,
): UseTourReturn {
  const { user } = useAuth();
  const employeeId = user?.employeeId ?? null;
  const key = tourStorageKey(tourId, employeeId);

  const [status, setStatus] = useState<TourStatus>(() => readStatus(key));
  const [active, setActive] = useState(false);

  // Re-read storage and stop the tour only on a *real* owner change
  // (login → logout, switch user, etc.). React 18 StrictMode replays
  // every effect on mount, so a naive `[key]`-scoped reset would clobber
  // a tour that the orchestrator just started in the same tick.
  const previousKeyRef = useRef(key);
  useEffect(() => {
    if (previousKeyRef.current === key) return;
    previousKeyRef.current = key;
    setStatus(readStatus(key));
    setActive(false);
  }, [key]);

  const start = useCallback(() => {
    // Only auto-replay when there's no record. Re-running a completed tour
    // is the orchestrator's job (clear the key, then call start).
    if (readStatus(key) !== 'unstarted') {
      return;
    }
    setActive(true);
  }, [key]);

  const skip = useCallback(() => {
    writeStatus(key, 'skipped');
    setStatus('skipped');
    setActive(false);
  }, [key]);

  const complete = useCallback(() => {
    writeStatus(key, 'completed');
    setStatus('completed');
    setActive(false);
  }, [key]);

  return { active, start, skip, complete, status };
}
